import React from 'react';
import MuiPopover, {
  PopoverProps as MuiPopoverProps,
} from '@mui/material/Popover';

/**
 * `PopoverProps` interface extends Material UI's `PopoverProps` and adds custom props.
 *
 * @extends MuiPopoverProps
 * @description This interface is used to define the props for the `Popover` component.
 */
export interface PopoverProps extends Omit<MuiPopoverProps, 'open'> {
  /** React element or component that will trigger the popover on hover */
  Component: React.ElementType;
  /** Content to be displayed inside the popover */
  children: React.ReactNode;
}

/**
 * `Popover` component that displays a popover when a mouse hover event occurs.
 * It accepts a trigger component and the content to show inside the popover.
 *
 * @param {PopoverProps} props - The properties passed to the component
 * @returns {React.ReactElement} The rendered `Popover` component with dynamic trigger and content
 *
 * @category Component
 * @document PopoverComponentUsage.md
 */
export const Popover = ({
  children,
  Component,
  ...popoverProps
}: PopoverProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  /**
   * Handles the opening of the popover when the mouse enters the component.
   *
   * @param {React.MouseEvent<HTMLElement>} event - The mouse enter event
   */
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handles the closing of the popover when the mouse leaves the component.
   */
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <Component
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      />
      <MuiPopover
        id="mouse-over-popover"
        sx={{ pointerEvents: 'none' }}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        {...popoverProps}
      >
        {children}
      </MuiPopover>
    </div>
  );
};
